import {
  APPOINTMENT_DEFAULT_DURATION,
  slotTaken,
  type AssignedInquiry,
  type PlannedInternalMeeting,
} from '@ghp/domain'

/**
 * Ce qui occupe déjà l'équipe, tel que la tablette l'a descendu : la tournée et ses points
 * internes (#669). Les écrans le lisent en cache local (`useTourOccupancy`), la pré-fiche le
 * compare au créneau que l'agent pose.
 */
export interface TourOccupancy {
  inquiries: readonly AssignedInquiry[]
  meetings: readonly PlannedInternalMeeting[]
}

/** Ce que le créneau recouvre : un rendez-vous de la tournée, ou un point interne. */
export type TakenTourSlot = { scheduledAt: string; durationMinutes: number } & (
  { inquiry: AssignedInquiry } | { meeting: PlannedInternalMeeting }
)

/**
 * La base ouverte est celle de l'équipe de la session (`useTeamData`) : tout ce qu'elle porte
 * l'occupe. Une seule équipe, donc — celle de la tournée, que `slotTaken` compare à elle-même.
 */
const TOUR_TEAM = { id: 'tour' }

/**
 * Le premier rendez-vous ou point interne de la tournée que ce créneau recouvre, durée comprise
 * (`slotTaken`), sans la demande qu'on planifie. `null` quand le créneau est libre.
 *
 * Un rendez-vous sans durée — une ligne d'avant #431 — compte une heure, comme sa carte.
 */
export function tourSlotTaken(
  tour: TourOccupancy,
  scheduledAt: string,
  durationMinutes: number,
  except: string,
): TakenTourSlot | null {
  const appointments = tour.inquiries.flatMap((inquiry) => {
    // `?? null` : une ligne en cache d'avant le champ le relit `undefined` (D22).
    const at = inquiry.scheduledAt ?? null

    return at === null
      ? []
      : [
          {
            scheduledAt: at,
            durationMinutes: inquiry.appointmentDurationMinutes ?? APPOINTMENT_DEFAULT_DURATION,
            team: TOUR_TEAM,
            inquiry,
          },
        ]
  })
  const meetings = tour.meetings.map((meeting) => ({
    id: meeting.id,
    scheduledAt: meeting.scheduledAt,
    durationMinutes: meeting.durationMinutes,
    team: TOUR_TEAM,
    meeting,
  }))

  return slotTaken(
    [...appointments, ...meetings],
    TOUR_TEAM.id,
    scheduledAt,
    durationMinutes,
    except,
  )
}
